/**
 * x-header directive.
 *
 * Manages the site header's transparency and interaction state. On the home
 * page (`template_name === "index"`), the header starts transparent when a
 * hero section is the first child of the page body. A GSAP ScrollTrigger
 * removes the transparency as the user scrolls past the hero.
 *
 * Applies CSS classes to the header based on hover/focus/expanded-menu state:
 *   `has-interactions`         - Applied while hovering, focused, or a mega menu is open.
 *   `is-transparent`           - Applied when the header is in transparent mode.
 *   `on-surface-light|dark`    - Applied based on the hero background colour when transparent.
 *
 * @options
 *   template_name - The Shopify template name (e.g. "index", "product").
 *
 * @example
 *   <header x-header="{ template_name: 'index' }">…</header>
 */
(() => {
  const root = (Alpine, el, { template_name }) => {
    /**
     * Determines whether the header should start in transparent mode.
     * Only applies on the index template when a hero section is first in the page.
     * @returns {boolean} True when the header should be transparent.
     */
    const hasTransparency = () => {
      if ("index" !== template_name) {
        return false;
      }

      return document.body.matches(":has(.section-hero:first-child)");
    };

    /**
     * Determines the on-surface colour token for the transparent header.
     * Returns "dark" when the hero slider has a dark background, "light" otherwise.
     * @returns {"light"|"dark"} The on-surface colour token.
     */
    const getTransparencyOnSurface = () => {
      if ("index" !== template_name) {
        return "light";
      }

      return document.body.matches(
        ":has(.section-hero:first-child .slider.on-surface-dark)"
      )
        ? "dark"
        : "light";
    };

    Alpine.bind(el, () => ({
      ["@mouseenter"]() {
        this.isHover = true;
      },

      ["@mouseleave"]() {
        this.isHover = false;
      },

      ["@focusin"]() {
        this.isFocus = true;
      },

      ["@focusout"]() {
        this.isFocus = false;
      },

      /** Sets `hasItemExpanded` when a mega menu opens. */
      ["@mega-menu-opened"]() {
        this.hasItemExpanded = true;
      },

      /** Clears `hasItemExpanded` when a mega menu begins closing. */
      ["@mega-menu-closing"]() {
        this.hasItemExpanded = false;
      },

      [":class"]() {
        return {
          "has-interactions": this.hasInteractions,
          "is-transparent": this.isTransparent,
          [`on-surface-${this.onSurfaceColor}`]: !this.hasInteractions,
        };
      },

      ["x-data"]() {
        let trigger = null;

        const transparency = hasTransparency();
        const onSurfaceColor = (transparency) =>
          transparency ? getTransparencyOnSurface() : "light";

        return {
          isHover: false,
          isFocus: false,
          hasItemExpanded: false,
          isTransparent: transparency,
          onSurfaceColor: onSurfaceColor(transparency),

          /**
           * Returns true when the header has any active interaction.
           * @returns {boolean} True when hovered, focused, or a menu is expanded.
           */
          get hasInteractions() {
            return this.isHover || this.isFocus || this.hasItemExpanded;
          },

          /**
           * Kills the ScrollTrigger instance on teardown.
           */
          destroy() {
            if (trigger) {
              trigger.kill();
            }
          },

          /**
           * Marks the header as ready and, when transparency is enabled,
           * creates a ScrollTrigger to toggle `isTransparent` as the page scrolls.
           */
          init() {
            el.dataset.headerReady = "";

            if (transparency) {
              const marker = (end) => {
                return () => `clamp(${window.innerHeight * 0.25} ${end})`;
              };

              this.$watch("isTransparent", (value) => {
                this.onSurfaceColor = onSurfaceColor(value);
              });

              trigger = this.$scrollTrigger.create({
                trigger: document.body,
                start: "top=-1px top",
                end: marker("top"),
                onEnterBack: () => {
                  this.isTransparent = true;
                },
                onEnter: () => {
                  this.isTransparent = true;
                },
                onLeave: () => {
                  this.isTransparent = false;
                },
                onLeaveBack: () => {
                  this.isTransparent = true;
                },
              });
            }
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("header", (el, { expression }, { evaluate }) => {
      root(Alpine, el, expression ? evaluate(expression) : {});
    });
  });
})();
